import type { Automation, Schedule } from '@harness/shared/protocol';
import styles from '../styles/automations.module.css';
import { scheduleText, TEXT } from '../texts';

const DAY_MS = 86_400_000;
const DAY_HEAD = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric' });

/** Monday 00:00 of the week that contains `now`, in the browser's time. */
function mondayOf(now: Date): Date {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
}

/** ISO 8601 calendar week, as German calendars count it. */
export function calendarWeek(monday: Date): number {
  // The week belongs to the year of its Thursday.
  const thursday = Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate() + 3);
  const yearStart = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1);
  return Math.floor((thursday - yearStart) / DAY_MS / 7) + 1;
}

function runsOn(schedule: Schedule, weekday: number): boolean {
  switch (schedule.kind) {
    case 'daily':
      return true;
    case 'weekdays':
      return weekday <= 5;
    case 'weekly':
      return schedule.weekday === weekday;
  }
}

/** This week's plan, as in the click demo: one row per automation, the time in each day it runs. */
export function WeekPlan({ automations, now }: { automations: Automation[]; now: Date }): React.JSX.Element {
  const monday = mondayOf(now);
  const days = [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({ weekday, date: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + weekday - 1) }));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return (
    <section className={styles.plan} aria-label={TEXT.weekPlan}>
      <div className={styles.legend}>
        <span className={styles.label}>{TEXT.thisWeek(calendarWeek(monday))}</span>
        <span className={styles.legendItem}>
          <span className={styles.swatchOn} aria-hidden="true" />
          {TEXT.plannedRun}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatchOff} aria-hidden="true" />
          {TEXT.pausedRun}
        </span>
      </div>
      <div className={styles.tableScroll}>
        <table className={styles.planTable}>
          <thead>
            <tr>
              <th scope="col">{TEXT.automation}</th>
              {days.map(({ weekday, date }) => (
                <th key={weekday} scope="col" aria-current={date.getTime() === today ? 'date' : undefined}>
                  {DAY_HEAD.format(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {automations.map((automation) => {
              const paused = automation.paused !== null || !automation.active;
              return (
                <tr key={automation.id}>
                  <th scope="row">
                    <strong>{automation.name}</strong>
                    <span className={styles.muted}>{scheduleText(automation.schedule)}</span>
                  </th>
                  {days.map(({ weekday }) => (
                    <td key={weekday}>
                      {runsOn(automation.schedule, weekday) ? (
                        <span className={paused ? styles.cellOff : styles.cellOn} title={paused ? TEXT.pausedRun : TEXT.plannedRun}>
                          {automation.schedule.time}
                        </span>
                      ) : (
                        <span className={styles.cellEmpty} />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
